import { useState, useEffect } from 'preact/hooks'
import analytics from '../utils/analytics'

// Helper function to parse natural timestamp format like "7:26 PM · May 30, 2025"
const parseNaturalTimestamp = (naturalTime) => {
  try {
    // Extract time and date parts
    const parts = naturalTime.split(' · ')
    if (parts.length !== 2) {
      // Fallback to ISO format if not in natural format
      return new Date(naturalTime)
    }
    
    const [timeStr, dateStr] = parts
    
    // Parse date (e.g., "May 30, 2025")
    const dateParts = dateStr.split(', ')
    const year = parseInt(dateParts[1])
    const monthDay = dateParts[0] // "May 30"
    
    // Parse time (e.g., "7:26 PM")
    const timeMatch = timeStr.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i)
    if (!timeMatch) {
      return new Date(naturalTime) // Fallback
    }
    
    let hours = parseInt(timeMatch[1])
    const minutes = parseInt(timeMatch[2])
    const ampm = timeMatch[3].toUpperCase()
    
    // Convert to 24-hour format
    if (ampm === 'PM' && hours !== 12) {
      hours += 12
    } else if (ampm === 'AM' && hours === 12) {
      hours = 0
    }
    
    // Create date string and parse
    const dateString = `${monthDay}, ${year} ${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:00`
    return new Date(dateString)
  } catch (error) {
    console.warn('Failed to parse natural timestamp:', naturalTime, error)
    return new Date() // Fallback to current time
  }
}

// Helper function to calculate relative time
const getRelativeTime = (timestamp) => {
  const now = new Date()
  const tweetTime = typeof timestamp === 'string' ? parseNaturalTimestamp(timestamp) : new Date(timestamp)
  const diffInMs = now - tweetTime
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInMinutes < 60) {
    return diffInMinutes <= 1 ? 'now' : `${diffInMinutes}m`
  } else if (diffInHours < 24) {
    return `${diffInHours}h`
  } else {
    return `${diffInDays}d`
  }
}

// Sample tweets data - in a real app, this would come from an API
const sampleTweets = [
  {
    id: 1,
    author: "@Curzey1",
    handle: "Curzey",
    content: "That's a wrap for #ShopifyEditions dev 2025 - so many great insights and fun to meet people IRL. How fucking great are all the new stuff the CLI, app dev and SQLite-stuff!?",
    timestamp: "4:00 PM · May 30, 2025",
    tweetUrl: "https://twitter.com/Curzey1/status/1234567890",
    likes: 42,
    retweets: 18
  },
  {
    id: 2,
    author: "@harleyf",
    handle: "Harley Finkelstein",
    content: `Welcome to 
@Shopify
 Editions Dot Dev 💚 

@ShopifyDevs
 👋

@tobi
 
@glencoates
 ✌️`,
    timestamp: "3:00 PM · May 30, 2025",
    tweetUrl: "https://x.com/harleyf/status/1928456088707354995",
    likes: 28,
    retweets: 12
  },
  {
    id: 3,
    author: "@pandectes",
    handle: "Pandectes",
    content: `🚀 We're live at the main event of #Shopify #EditionsDev 2025!

The energy is electric ⚡️
The future of commerce is being written, and we're right here for it.

Let's build what's next 🛠️💡
#ShopifyEditions #DevLife #ecommerce`,
    timestamp: "8:51 AM · May 30, 2025",
    tweetUrl: "https://x.com/pandectes/status/1928434115252035674",
    likes: 67,
    retweets: 31
  },
  {
    id: 4,
    author: "@zapiet",
    handle: "Zapiet",
    content: `Nothing like a live demo to keep things exciting 😅💻 
@ShopifyDevs
 #editionsdev`,
    timestamp: "9:31 AM · May 30, 2025",
    tweetUrl: "https://x.com/zapiet/status/1928444052577361928",
    likes: 35,
    retweets: 19
  },
  {
    id: 5,
    author: "@marktanft",
    handle: "mj",
    content: `Feels great to be at an event with this much energy! Excited to meet the 
@ShopifyDevs
 community for an all-day deep dive into what's new and what's next.

From storefront components to next-gen dev tools and AI building blocks. See you around!`,
    timestamp: "9:59 AM · May 30, 2025",
    tweetUrl: "https://x.com/marktanft/status/1928451160534380620",
    likes: 51,
    retweets: 24
  },
  {
    id: 6,
    author: "Nick Wesselman 💙💛",
    handle: "@nick_wesselmang",
    content: `Thank you Shopify http://Editions.dev attendees! Loved building with you all in our masterclass today. Tons of positive and constructive feedback on the new \`app dev\` tool.`,
    timestamp: "5:51 PM · May 30, 2025",
    tweetUrl: "https://x.com/nick_wesselman/status/1928570089160290463",
    likes: 73,
    retweets: 41
  },
  {
    id: 7,
    author: "Justin Sucher",
    handle: "@JSucher",
    content: `After attending my first Shopify Editions as a merchant, I am leaving with a few takeaways:

1) There's no better forum to meet and talk with the people building on and within the platform.  Meeting the people and partners bringing these tools to life was worth the price of admission alone.  I would recommend any curious Shopify operators attend in the future.

2) There's no better place to be building a business.  I've been a Shopify customer for the last 12 years and the platform has never been in a better position than it is today for a merchant to grow and scale their e-commerce, retail or wholesale business. 

3) If you can't build it now, you probably will be able to soon.  Between generative AI and the incredible partner ecosystem, I'm running into less obstacles than ever when building systems and tools for our brand.  The possibilities of what you can do have never been greater.

Thank you 
@Shopify
 for hosting this fantastic event! 
@harleyf
 
@glencoates
 
@tobi`,
    timestamp: "3:44 PM · May 30, 2025",
    tweetUrl: "http://x.com/JSucher/status/1928538001686081753",
    likes: 29,
    retweets: 15
  },
  {
    id: 8,
    author: "Kurt Elster",
    handle: "@kurtinc",
    content: `That's a wrap on 
    @Shopify
     Editions .dev '25!! Always fun to meet my internet friends IRL`,
    timestamp: "7:22 PM · May 30, 2025",
    tweetUrl: "https://x.com/kurtinc/status/1928592828986188175",
    likes: 88,
    retweets: 52
  }
]

export function SocialFeed() {
  const [currentTweetIndex, setCurrentTweetIndex] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)

  // Auto-scroll through tweets
  useEffect(() => {
    const interval = setInterval(() => {
      setIsAnimating(true)
      setTimeout(() => {
        setCurrentTweetIndex((prev) => (prev + 1) % sampleTweets.length)
        setIsAnimating(false)
      }, 300) // Half of transition duration
    }, 4000) // Change tweet every 4 seconds

    return () => clearInterval(interval)
  }, [])

  const handleSubmitTweetClick = () => {
    analytics.trackEvent('social_feed_submit_request', {
      event_category: 'social_engagement',
      event_label: 'tweet_submission_request'
    })
    
    // Ask user for email since we don't have one configured
    const userEmail = prompt('Please enter the email address where you\'d like to receive tweet submissions:')
    
    if (!userEmail) {
      return // User cancelled
    }
    
    // Open email with pre-filled subject and body
    const subject = encodeURIComponent('Submit My Tweet for Editions Vibes App')
    const body = encodeURIComponent(`Hi Editions Vibes Team!

I'd love to have my tweet featured in the social feed. Here are the details:

Tweet Content: [Your tweet about Shopify Editions]
Your Name: [Your name]
Your Handle: @[your_handle]
Link to Tweet: [Direct link to your tweet]

Thanks for creating such an amazing conference companion app!

Best regards,
[Your name]`)
    
    window.open(`mailto:${userEmail}?subject=${subject}&body=${body}`, '_blank')
  }

  const handleTweetClick = (tweetUrl) => {
    if (tweetUrl) {
      analytics.trackEvent('social_feed_tweet_click', {
        event_category: 'social_engagement',
        event_label: 'original_tweet_view'
      })
    }
  }

  const currentTweet = sampleTweets[currentTweetIndex]

  return (
    <div className="bg-gradient-to-r from-editions-dark-light/50 to-editions-purple/10 border border-editions-purple/20 rounded-xl p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-semibold text-sm flex items-center gap-2">
          <span className="text-lg">🐦</span>
          Community Buzz
        </h3>
        <button
          onClick={handleSubmitTweetClick}
          className="text-xs bg-editions-orange/20 text-editions-orange border border-editions-orange/40 px-3 py-1 rounded-full hover:bg-editions-orange/30 transition-colors"
        >
          Submit Your Tweet
        </button>
      </div>
      
      <div className="relative overflow-hidden">
        <div 
          className={`transition-all duration-600 ease-in-out ${
            isAnimating ? 'opacity-0 transform translate-y-2' : 'opacity-100 transform translate-y-0'
          }`}
        >
          <a
            href={currentTweet.tweetUrl || "#"}
            target={currentTweet.tweetUrl ? "_blank" : undefined}
            rel={currentTweet.tweetUrl ? "noopener noreferrer" : undefined}
            onClick={currentTweet.tweetUrl ? () => handleTweetClick(currentTweet.tweetUrl) : (e) => e.preventDefault()}
            className={`block bg-white/5 border border-white/10 rounded-lg p-3 transition-colors ${
              currentTweet.tweetUrl ? 'hover:bg-white/10' : ''
            }`}
          >
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-editions-orange to-editions-purple rounded-full flex items-center justify-center flex-shrink-0">
                <span className="text-white text-xs font-bold">
                  {currentTweet.handle.charAt(0).toUpperCase()}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex flex-col gap-1 mb-1">
                  <span className="text-white text-sm font-medium truncate">
                    {currentTweet.handle}
                  </span>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400 text-xs">
                      {currentTweet.author}
                    </span>
                    <span className="text-gray-500 text-xs">•</span>
                    <span className="text-gray-500 text-xs">
                      {getRelativeTime(currentTweet.timestamp)}
                    </span>
                    {currentTweet.tweetUrl && (
                      <>
                        <span className="text-gray-500 text-xs">•</span>
                        <svg className="w-3 h-3 text-editions-orange hover:text-editions-orange/80" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </>
                    )}
                  </div>
                </div>
                
                <p className="text-gray-200 text-sm leading-relaxed mb-2">
                  {currentTweet.content}
                </p>
                
                <div className="flex items-center gap-4 text-xs text-gray-400">
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                    </svg>
                    <span>{currentTweet.likes}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    <span>{currentTweet.retweets}</span>
                  </div>
                </div>
              </div>
            </div>
          </a>
        </div>
      </div>
      
      {/* Progress indicator */}
      <div className="flex justify-center mt-3 gap-1">
        {sampleTweets.map((_, index) => (
          <div
            key={index}
            className={`w-1.5 h-1.5 rounded-full transition-colors duration-300 ${
              index === currentTweetIndex 
                ? 'bg-editions-orange' 
                : 'bg-gray-600'
            }`}
          />
        ))}
      </div>
    </div>
  )
}

export function Footer() {
  return (
    <footer className="bg-editions-dark-light/50 border-t border-editions-purple/30 mt-8 py-6">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <div className="space-y-3">
          <p className="text-gray-300 text-sm">
            Built with lots of love ❤️{' '}
            <a 
              href="https://www.linkedin.com/in/ivalsaraj/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-editions-purple hover:text-editions-purple-light transition-colors underline"
            >
              @ivalsaraj
            </a>
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-xs text-gray-400">
            <span>Need quick changes to the site?</span>
            <a 
              href="mailto:raj@helixo.co?subject=Editions Vibes - Quick Changes Request"
              className="text-editions-orange hover:text-editions-orange/80 transition-colors underline flex items-center gap-1"
            >
              <span>📧</span>
              raj@helixo.co
            </a>
          </div>
        </div>
      </div>
    </footer>
  )
} 